import { Component, createSignal, Show, onMount } from 'solid-js';
import { useAuth } from '../../stores/AuthContext';
import { socketState } from '../../services/socket';
import { getLatestNews, newsState } from '../../services/newsService';
import { TbMenu2, TbMoon, TbSun, TbLogout, TbArrowLeft, TbChevronRight, TbChevronLeft, TbBell } from 'solid-icons/tb';

interface HeaderProps {
  onMenuClick: () => void;
  sidebarOpen: boolean;
  rightSidebarOpen: (() => boolean) | boolean;
  onRightSidebarToggle: () => void;
}

/**
 * Header Component
 * Top navigation bar with menu toggle, dark mode, and user menu
 */
const Header: Component<HeaderProps> = (props) => {
  const auth = useAuth();
  const [darkMode, setDarkMode] = createSignal(true);
  const [userMenuOpen, setUserMenuOpen] = createSignal(false);

  // Initialize dark mode from localStorage (default to true/dark mode)
  onMount(() => {
    const savedMode = localStorage.getItem('darkMode');
    // Default to dark mode if not explicitly saved as false
    const isDarkMode = savedMode === null ? true : savedMode === 'true';
    setDarkMode(isDarkMode);
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    }

    // Fetch latest news on component mount
    getLatestNews();
  });

  const toggleDarkMode = () => {
    const newMode = !darkMode();
    setDarkMode(newMode);
    localStorage.setItem('darkMode', String(newMode));

    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await auth.logout();
  };

  return (
    <header class={`fixed top-0 left-0 right-0 z-10 bg-white/40 dark:bg-dark/40 backdrop-blur-xl h-16 shadow-light dark:shadow-none border-b border-dashed border-gray-200 dark:border-gray-700 transition-all duration-300 ${props.sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'
      } ${typeof props.rightSidebarOpen === 'function' ? (props.rightSidebarOpen() ? 'lg:mr-80' : 'lg:mr-0') : (props.rightSidebarOpen ? 'lg:mr-80' : 'lg:mr-0')}`}>
      <div class="h-full px-4 md:px-6 flex items-center justify-between relative">
        {/* Left Side - Menu Button */}
        <div class="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <button
            onClick={props.onMenuClick}
            class="flex items-center justify-center p-1.5 md:p-2 text-gray-700 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400 transition-colors z-50 flex-shrink-0 font-body"
            title={props.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <Show when={props.sidebarOpen} fallback={<TbMenu2 size={24} class="md:w-[28px] md:h-[28px]" />}>
              <TbArrowLeft size={24} class="md:w-[28px] md:h-[28px]" />
            </Show>
          </button>
        </div>

        {/* Center - Live Status - Absolutely centered */}
        <div class="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          {/* Socket Status */}
          <div class="flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-white/30 dark:bg-white/20 backdrop-blur-[40px] border border-white/40 dark:border-white/30 shadow-card">
            <div
              class={`w-2.5 h-2.5 rounded-full animate-pulse ${socketState.isConnected() ? 'bg-green-500' : 'bg-yellow-500'
                }`}
            />
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-900 dark:text-white">
              {socketState.isConnected() ? 'Live' : 'Connecting ...'}
            </span>
          </div>
        </div>

        {/* Right Side - Actions */}
        <div class="flex items-center gap-2 flex-shrink-0">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            class="p-2 rounded-lg text-gray-700 dark:text-gray-100 hover:bg-white/20 dark:hover:bg-white/20 transition-colors"
            title={darkMode() ? 'Light mode' : 'Dark mode'}
          >
            <Show when={darkMode()} fallback={<TbMoon size={20} />}>
              <TbSun size={20} />
            </Show>
          </button>

          {/* Notification Bell */}
          <button
            onClick={async () => {
              await getLatestNews();
              newsState.setShowModal(true);
            }}
            class="relative p-2 rounded-lg text-gray-700 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-white/20 dark:hover:bg-white/20 transition-colors"
            title="View news and announcements"
          >
            <TbBell size={20} />
            <Show when={newsState.unreadCount() > 0}>
              <span class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            </Show>
          </button>

          <button
            onClick={props.onRightSidebarToggle}
            class="hidden lg:flex items-center justify-center p-2 text-gray-700 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400 transition-colors z-50 flex-shrink-0 font-body"
            title={((() => {
              const isOpen = typeof props.rightSidebarOpen === 'function' ? props.rightSidebarOpen() : props.rightSidebarOpen;
              return isOpen ? 'Hide right panel' : 'Show right panel';
            })())}
          >
            <Show when={typeof props.rightSidebarOpen === 'function' ? props.rightSidebarOpen() : props.rightSidebarOpen} fallback={<TbChevronLeft size={24} />}>
              <TbChevronRight size={24} />
            </Show>
          </button>

          {/* User Menu Toggle
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen())}
            class="flex items-center justify-center p-2 text-gray-700 dark:text-gray-100 hover:bg-white/20 dark:hover:bg-white/20 transition-colors rounded-lg font-body"
            title="User menu"
          >
            <div class="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-semibold">
              U
            </div>
          </button>

          <Show when={userMenuOpen()}>
            <div class="absolute top-16 right-4 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <button
                onClick={handleLogout}
                class="w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors text-sm font-semibold"
                title="Logout"
              >
                <TbLogout size={18} />
                Logout
              </button>
            </div>
          </Show>
          */}
        </div>

      </div>

    </header>
  );
};

export default Header;
